// Company-wide display time zone is America/New_York. The backend already
// returns pre-converted local strings ("2026-08-19T05:11"), so these helpers
// only format — they never convert time zones (avoids double-shifting).
export const COMPANY_TZ_LABEL = "ET";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatClockTime(localStr) {
    if (!localStr) return "—";
    const timePart = localStr.slice(11, 16);
    if (!timePart) return "—";
    const [h, m] = timePart.split(":").map(Number);
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function formatWorkDate(dateStr) {
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return dateStr;
    return `${MONTHS[m - 1]} ${d}, ${y}`;
}

export function formatShortDate(dateStr) {
    if (!dateStr) return "—";
    const [, m, d] = dateStr.slice(0, 10).split("-").map(Number);
    if (!m || !d) return dateStr;
    return `${MONTHS[m - 1]} ${d}`;
}

export function todayLocalDate() {
    const now = new Date();
    // en-CA gives YYYY-MM-DD
    return now.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

export function elapsedSince(utcIso) {
    if (!utcIso) return "";
    const start = new Date(utcIso).getTime();
    const seconds = Math.max(0, Math.floor((Date.now() - start) / 1000));
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

export function formatSeconds(seconds) {
    if (seconds === null || seconds === undefined || seconds < 0) return "";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

const DAY_MS = 86400000;

// Date-only arithmetic done in UTC so it never drifts across time zones.
function toUtc(dateStr) {
    const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
    return Date.UTC(y, m - 1, d);
}

export function addDays(dateStr, n) {
    return new Date(toUtc(dateStr) + n * DAY_MS).toISOString().slice(0, 10);
}

/** Monday of the week containing dateStr. */
export function startOfWeek(dateStr) {
    const dow = new Date(toUtc(dateStr)).getUTCDay(); // 0=Sun
    return addDays(dateStr, dow === 0 ? -6 : 1 - dow);
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function weekdayLabel(dateStr) {
    return DOW[new Date(toUtc(dateStr)).getUTCDay()];
}

/** Renders an audit-trail value: datetime strings become friendly local times. */
export function formatAuditValue(value) {
    if (typeof value !== "string") return value;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
        return `${formatShortDate(value)} ${formatClockTime(value)}`;
    }
    return value;
}

export function readGpsPosition() {
    return new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
            (pos) =>
                resolve({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    accuracy: pos.coords.accuracy ?? null,
                }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
        );
    });
}
